import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Shield, Settings, X } from 'lucide-react';

const CONSENT_KEY = 'ci_cookie_consent';

const getConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
};

const setConsent = (prefs) => {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...prefs, timestamp: Date.now() }));
};

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, functional: true, analytics: false });

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    setConsent({ essential: true, functional: true, analytics: true });
    setVisible(false);
  };

  const handleRejectNonEssential = () => {
    setConsent({ essential: true, functional: false, analytics: false });
    setVisible(false);
  };

  const handleSavePrefs = () => {
    setConsent(prefs);
    setVisible(false);
    setShowPrefs(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] p-4 sm:p-6 pointer-events-none [&>*]:pointer-events-auto" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        {!showPrefs ? (
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#2a3e5d] flex items-center justify-center shrink-0">
                <Shield className="h-5 w-5 text-[#02d1d4]" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-[#2a3e5d] text-sm mb-1">We value your privacy</h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  We use cookies to ensure the platform works correctly, remember your preferences, and understand how you use our service. 
                  Read our <Link to="/cookies-policy" className="text-[#20acf6] hover:underline">Cookies Policy</Link> for details.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4 sm:justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPrefs(true)}
                className="text-xs gap-1.5"
                data-testid="cookie-manage-btn"
              >
                <Settings className="h-3.5 w-3.5" /> Manage Preferences
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectNonEssential}
                className="text-xs"
                data-testid="cookie-reject-btn"
              >
                Reject Non-Essential
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="bg-[#20acf6] hover:bg-[#3bb8f7] text-xs"
                data-testid="cookie-accept-btn"
              >
                Accept All
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#2a3e5d] text-sm">Cookie Preferences</h3>
              <button onClick={() => setShowPrefs(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-[#2a3e5d]">Essential Cookies</span>
                  <p className="text-[11px] text-slate-500">Required for login, security, and basic functionality</p>
                </div>
                <input type="checkbox" checked disabled className="accent-[#20acf6] h-4 w-4" />
              </label>
              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-[#2a3e5d]">Functional Cookies</span>
                  <p className="text-[11px] text-slate-500">Remember preferences like text size and display settings</p>
                </div>
                <input type="checkbox" checked={prefs.functional} onChange={(e) => setPrefs(p => ({ ...p, functional: e.target.checked }))} className="accent-[#20acf6] h-4 w-4" data-testid="cookie-pref-functional" />
              </label>
              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-[#2a3e5d]">Analytics Cookies</span>
                  <p className="text-[11px] text-slate-500">Help us understand usage to improve the platform</p>
                </div>
                <input type="checkbox" checked={prefs.analytics} onChange={(e) => setPrefs(p => ({ ...p, analytics: e.target.checked }))} className="accent-[#20acf6] h-4 w-4" data-testid="cookie-pref-analytics" />
              </label>
            </div>
            <div className="flex gap-2 mt-4 justify-end">
              <Button variant="outline" size="sm" onClick={handleRejectNonEssential} className="text-xs">
                Essential Only
              </Button>
              <Button size="sm" onClick={handleSavePrefs} className="bg-[#20acf6] hover:bg-[#3bb8f7] text-xs" data-testid="cookie-save-prefs-btn">
                Save Preferences
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;
